import React, { useState, useEffect, useRef } from 'react';
import { Stack, Text, Spinner, SpinnerSize, MessageBar, MessageBarType, Separator } from '@fluentui/react';
import { 
  DatabaseRegular,
  SettingsRegular,
  SearchRegular,
  LightbulbRegular,
  BrainCircuitRegular,
  CloudRegular,
  DocumentTextRegular,
  TagRegular,
  QuestionCircleRegular,
  CheckmarkCircleRegular,
  WarningRegular,
  InfoRegular
} from '@fluentui/react-icons';
import { authenticatedFetch } from '../../utils/authApi';
import styles from './AdminGuide.module.css';

// Types for tile-based content from Cosmos
interface BaseTile {
  id: string;
  type: string;
}

interface ConnectorTile extends BaseTile {
  type: 'connector';
  icon: string;
  name: string;
  description: string;
  configItems: string[];
  bestFor: string;
}

interface InfoBoxTile extends BaseTile {
  type: 'info-box';
  variant: 'info' | 'concept' | 'tip' | 'troubleshoot';
  title: string;
  content: string;
  listItems?: string[];
}

interface StepGuideTile extends BaseTile {
  type: 'step-guide';
  stepNumber: number;
  title: string;
  content: string;
  listItems?: string[];
}

type Tile = ConnectorTile | InfoBoxTile | StepGuideTile;

interface GuideSection {
  id: string;
  title: string;
  iconName: string;
  intro: string;
  tiles: Tile[];
  lastModified?: string;
}

const iconMap: Record<string, React.ReactNode> = {
  'database': <DatabaseRegular className={styles.navIcon} />,
  'cloud': <CloudRegular className={styles.navIcon} />,
  'brain': <BrainCircuitRegular className={styles.navIcon} />,
  'search': <SearchRegular className={styles.navIcon} />,
  'settings': <SettingsRegular className={styles.navIcon} />,
  'lightbulb': <LightbulbRegular className={styles.navIcon} />,
  'document': <DocumentTextRegular className={styles.navIcon} />,
  'tag': <TagRegular className={styles.navIcon} />,
  'question': <QuestionCircleRegular className={styles.navIcon} />
};

const AdminGuide: React.FC = () => {
  const [sections, setSections] = useState<GuideSection[]>([]);
  const [selectedSection, setSelectedSection] = useState<string>('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadSections();
  }, []);

  // Scroll to top when section changes
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [selectedSection]);

  const loadSections = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await authenticatedFetch('/api/help/admin-guide');
      const data = await response.json();
      
      if (data.success && data.sections) {
        setSections(data.sections);
        // Set first section as selected if no selection
        if (data.sections.length > 0 && !data.sections.find((s: GuideSection) => s.id === selectedSection)) {
          setSelectedSection(data.sections[0].id);
        }
      } else {
        throw new Error(data.error || 'Failed to load admin guide');
      }
    } catch (err) {
      console.error('Error loading admin guide:', err);
      setError(err instanceof Error ? err.message : 'Failed to load admin guide content');
    } finally {
      setLoading(false);
    }
  };

  const renderTile = (tile: Tile) => {
    switch (tile.type) {
      case 'connector':
        return (
          <div key={tile.id} className={styles.connectorCard}>
            <div className={styles.connectorHeader}>
              <span className={styles.connectorIcon}>{tile.icon}</span>
              <Text className={styles.connectorName}>{tile.name}</Text>
            </div>
            <Text className={styles.connectorDesc}>{tile.description}</Text>
            <div className={styles.connectorConfig}>
              <Text className={styles.configTitle}>Configuration:</Text>
              <ul>
                {tile.configItems.map((item, idx) => (
                  <li key={idx} dangerouslySetInnerHTML={{ __html: item.replace(/^([^:]+):/, '<strong>$1:</strong>') }} />
                ))}
              </ul>
            </div>
            <div className={styles.useCaseBox}>
              <Text className={styles.useCaseTitle}>Best For:</Text>
              <Text>{tile.bestFor}</Text>
            </div>
          </div>
        );

      case 'info-box':
        const variantClass = {
          'info': styles.infoBox,
          'concept': styles.conceptBox,
          'tip': styles.tipBox,
          'troubleshoot': styles.troubleItem
        }[tile.variant] || styles.infoBox;

        if (tile.variant === 'troubleshoot') {
          return (
            <div key={tile.id} className={styles.troubleItem}>
              <Text className={styles.troubleQuestion}>{tile.title}</Text>
              <Text className={styles.troubleAnswer}>{tile.content}</Text>
            </div>
          );
        }

        return (
          <div key={tile.id} className={variantClass}>
            <Text className={tile.variant === 'concept' ? styles.conceptTitle : tile.variant === 'tip' ? styles.tipTitle : styles.infoTitle}>
              {tile.title}
            </Text>
            {tile.content && <Text>{tile.content}</Text>}
            {tile.listItems && tile.listItems.length > 0 && (
              <ul className={styles.stepList}>
                {tile.listItems.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
            )}
          </div>
        );

      case 'step-guide':
        return (
          <div key={tile.id} className={styles.guideStep}>
            <div className={styles.guideStepHeader}>
              <span className={styles.guideStepNum}>Step {tile.stepNumber}</span>
              <Text className={styles.guideStepTitle}>{tile.title}</Text>
            </div>
            {tile.content && (
              <Text className={styles.guideStepContent}>{tile.content}</Text>
            )}
            {tile.listItems && tile.listItems.length > 0 && (
              <ul className={styles.guideList}>
                {tile.listItems.map((item, idx) => (
                  <li key={idx} dangerouslySetInnerHTML={{ __html: item.replace(/^([^:]+):/, '<strong>$1:</strong>') }} />
                ))}
              </ul>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  const renderSectionContent = (section: GuideSection) => {
    // Check if this section has connectors (for grid layout)
    const hasConnectors = section.tiles.some(t => t.type === 'connector');
    // Check if this section has step guides (for step layout)
    const hasSteps = section.tiles.some(t => t.type === 'step-guide');

    return (
      <div className={styles.sectionContent}>
        <Text className={styles.intro}>{section.intro}</Text>
        
        {hasConnectors ? (
          <div className={styles.connectorGrid}>
            {section.tiles.filter(t => t.type === 'connector').map(renderTile)}
          </div>
        ) : hasSteps ? (
          <div className={styles.stepGuide}>
            {section.tiles.map(renderTile)}
          </div>
        ) : (
          <>
            {section.tiles.map(renderTile)}
          </>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <Stack horizontalAlign="center" verticalAlign="center" style={{ height: '400px' }}>
          <Spinner size={SpinnerSize.large} />
          <Text style={{ marginTop: '16px' }}>Loading admin guide...</Text>
        </Stack>
      </div>
    );
  }

  const currentSection = sections.find(s => s.id === selectedSection);

  return (
    <div className={styles.container}>
      {error && (
        <MessageBar 
          messageBarType={MessageBarType.error} 
          onDismiss={() => setError(null)}
          className={styles.errorBar}
        >
          {error}
        </MessageBar>
      )}

      <div className={styles.layout}>
        {/* Sidebar Navigation */}
        <div className={styles.sidebar}>
          <div className={styles.sidebarHeader}>
            <Text className={styles.sidebarTitle}>Configuration Guide</Text>
          </div>
          
          <nav className={styles.nav}>
            {sections.map((section) => (
              <div
                key={section.id}
                className={`${styles.navItem} ${selectedSection === section.id ? styles.navItemActive : ''}`}
                onClick={() => setSelectedSection(section.id)}
              >
                <span className={styles.navIconWrapper}>
                  {iconMap[section.iconName] || <SettingsRegular className={styles.navIcon} />}
                </span>
                <div className={styles.navText}>
                  <Text className={styles.navTitle}>{section.title}</Text>
                </div>
              </div>
            ))}
          </nav>
          
          <div className={styles.sidebarFooter}>
            <InfoRegular className={styles.footerIcon} />
            <Text className={styles.footerText}>
              Click a section to view detailed configuration guidance
            </Text>
          </div>
        </div>

        {/* Main Content Area */}
        <div className={styles.mainContent} ref={contentRef}>
          {currentSection ? (
            <>
              <div className={styles.contentHeader}>
                <div className={styles.headerIcon}>
                  {iconMap[currentSection.iconName] || <SettingsRegular />}
                </div>
                <div className={styles.headerText}>
                  <Text className={styles.contentTitle}>{currentSection.title}</Text>
                  <Text className={styles.contentIntro}>{currentSection.intro}</Text>
                </div>
              </div>
              
              <Separator className={styles.separator} />
              
              <div className={styles.contentBody}>
                {renderSectionContent(currentSection)}
              </div>
            </>
          ) : (
            <div className={styles.emptyState}>
              <SettingsRegular className={styles.emptyIcon} />
              <Text className={styles.emptyText}>Select a section from the sidebar to view configuration guidance</Text>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminGuide;
